<template>
  <div>
    <div>
      <InputGroup class="w-100 flex">
        <InputRow
          v-slot="{ field: inputField, validation: { valid } }"
          v-model="commentUserIdRef"
          name="commentUserId"
          hide-error-message
          required
        >
          <InputGroupAddon
            class="p-0"
            :class="valid == null ? undefined : valid ? 'p-valid' : 'p-invalid'"
            style="border-width: 1px"
          >
            <SearchSelect
              v-if="sitePermissionFilter != null"
              v-bind="inputField"
              v-model="commentUserIdRef"
              class="inputGroupBaseSelect"
              :state="valid"
              :mode="SelectMode.USER"
              :placeholder="t('components.shared.searchSelect.UserSelect.selectUser').toString()"
              :filters="
                siteId == null
                  ? undefined
                  : {
                      hasRightsOnSite: {
                        siteIds: [siteId],
                        rights: SQDC_ASSING_MINIMUM_RIGHTS,
                      },
                    }
              "
              :hide-jump-to-origin="true"
              :required="true"
              @update:model-value="handleInput"
            />
          </InputGroupAddon>
        </InputRow>

        <InputRow
          v-slot="{ field: inputField, validation: { valid } }"
          v-model="comment"
          name="comment"
          hide-error-message
          required
        >
          <PrimeVueTextarea
            ref="commentFocus"
            v-bind="inputField"
            :class="valid ? 'p-valid' : 'p-invalid'"
            rows="1"
            class="taskCreateCommentInput flex-1"
            @keydown.ctrl.enter="addComment"
            @keydown.meta.enter="addComment"
            @keydown.esc="$emit('commentAdded')"
            @paste.stop
          />
        </InputRow>
        <InputGroupAddon class="p-0">
          <LISAButton
            class="saveTaskCommentButton p-button-success p-button-outlined"
            :disabled="validation.invalid"
            @click="addComment"
          >
            {{ t("components.event-action.TaskCreateComment.send") }}
          </LISAButton>
        </InputGroupAddon>
      </InputGroup>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import InputRow from "@/components/shared/form/InputRow.vue";
import { ref, nextTick, computed, onMounted } from "vue";
import LISAButton from "@LISAPrime/LISAButton.vue";
import PrimeVueTextarea from "primevue/textarea";
import { useAddTaskCommentMutation } from "@/graphql";
import store from "@/store";
import { useFormValidation } from "@/modules/formValidation";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import {
  useProvidedBoardContext,
  useSpecifiedBoardContext,
} from "@/modules/sqdc/helper/EABConfigHelper";
import SearchSelect from "@/components/shared/searchSelects/SearchSelect.vue";
import { SelectMode } from "@/components/shared/searchSelects/SelectDefinitions";
import { SQDC_ASSING_MINIMUM_RIGHTS } from "./helper/SQDCHelper";

const props = defineProps({
  taskId: {
    type: Number,
    required: true,
  },
  originalBoardId: {
    type: Number,
    required: false,
    default: undefined,
  },
});
const emit = defineEmits({ commentAdded: null, "update:modelValue": null });
const comment = ref("");
const commentFocus = ref<InstanceType<typeof PrimeVueTextarea>>();
onMounted(() => {
  if (commentFocus.value != null && "$el" in commentFocus.value) {
    const el = commentFocus.value?.$el as HTMLTextAreaElement;
    el.focus();
  }
});
const isAnonym = ref<boolean>(store.getters["login/isAnonym"]);
const userId = ref<number | null>(isAnonym.value ? null : Number(store.getters["login/userId"]));

const commentUserId = computed<number>({
  get() {
    return store.getters["taskComment/getTaskCommentatorId"];
  },
  set(value: number) {
    userId.value = value;
    emit("update:modelValue", userId.value);
  },
});
const commentUserIdRef = ref(commentUserId.value);
const {
  mutate: addCommentMutation,
  onDone,
  loading,
} = useAddTaskCommentMutation(() => ({
  variables: {
    taskId: props.taskId,
    comment: comment.value,
    commentUserId: commentUserIdRef.value as number,
  },
}));
onDone(() => {
  emit("commentAdded");
});

async function handleInput(id: number | undefined) {
  emit("update:modelValue", id);
  if (id == null || isAnonym.value === false) {
    return;
  }
  await store.dispatch("taskComment/setTaskCommentatorId", id);
  await nextTick();
}
const validation = useFormValidation({ name: "TaskCreateComponent" });

async function addComment() {
  if (!loading.value && (await validation.validate())) {
    await addCommentMutation();
  }
}

const { siteId } = useProvidedBoardContext();
const { siteId: originalSiteId } = useSpecifiedBoardContext(computed(() => props.originalBoardId));

const sitePermissionFilter = computed(() => {
  const siteIdLocal = siteId.value ?? originalSiteId.value;
  if (siteIdLocal == null) {
    return;
  }
  return siteIdLocal;
});

const { t } = useTranslation();
</script>

<style lang="scss" scoped>
.taskCreateCommentInput {
  font-size: 1.25em;
}

.saveTaskCommentButton {
  font-size: 1.25em;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
</style>

<style lang="scss">
.inputGroupBaseSelect {
  > .p-multiselect {
    border: none !important;
  }
  > .multiselect {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;

    .selected {
      font-size: 0.75em;
    }
  }
}
</style>
